import type { EditorDefaultFormValues } from "./schema-builder-field-editor-default.type";
import type {
  GetEditorValuesOptions,
  SchemaBuilderFieldEditorForm,
} from "../schema-builder-field-editor.type";
import type { SchemaFieldOptions } from "@/entities/schema";

import isEqual from "lodash/isEqual";

import { isEqualString } from "@/utilities/string";

import {
  getConditionsFormValuesFromSchema,
  getSchemaConditionsFromValues,
} from "../../schema-builder-condition";
import {
  generateFieldUniqueModel,
  isNewSchemaField,
} from "../../schema-builder.helper";

/**
 * It generates form values for default fields based initial `schema`.
 * ⚠️ Pass `form` to also use existing form values depending on the context.
 */
export const getEditorDefaultValues = ({
  fieldType,
  schema,
  form,
}: GetEditorValuesOptions): EditorDefaultFormValues => {
  const valuesToKeep = getEditorDefaultValuesTouched(form);

  const isNewField = isNewSchemaField(schema);

  const schemaModel = isNewField
    ? generateFieldUniqueModel(fieldType)
    : schema.model;

  return {
    label: valuesToKeep.label ?? schema.label,
    model: valuesToKeep.model ?? schemaModel,
    placeholder: valuesToKeep.placeholder ?? schema.placeholder,
    hint: valuesToKeep.hint ?? schema.hint,
    readOnly: valuesToKeep.readOnly ?? schema.readOnly,
    conditions:
      valuesToKeep.conditions ??
      getConditionsFormValuesFromSchema(schema.visible),
  };
};

export const getEditorDefaultValuesTouched = (
  form?: SchemaBuilderFieldEditorForm
): Partial<EditorDefaultFormValues> => {
  if (!form) return {};

  const touchedValues: Partial<EditorDefaultFormValues> = {};
  const existingValues = form.getFieldsValue();

  if (form.isFieldTouched("label") && existingValues.label)
    touchedValues.label = existingValues.label;

  if (form.isFieldTouched("model") && existingValues.model)
    touchedValues.model = existingValues.model;

  if (form.isFieldTouched("placeholder") && existingValues.placeholder)
    touchedValues.placeholder = existingValues.placeholder;

  if (form.isFieldTouched("hint") && existingValues.hint)
    touchedValues.hint = existingValues.hint;

  if (form.isFieldTouched("readOnly") && existingValues.readOnly)
    touchedValues.readOnly = existingValues.readOnly;

  if (form.isFieldTouched("conditions") && existingValues.conditions)
    touchedValues.conditions = existingValues.conditions;

  return touchedValues;
};

export const isEditorDefaultFormValuesEqual = (
  a: EditorDefaultFormValues,
  b: EditorDefaultFormValues
) => {
  if (!isEqualString(a.label, b.label)) return false;
  if (!isEqualString(a.model, b.model)) return false;
  if (!isEqualString(a.placeholder, b.placeholder)) return false;
  if (!isEqualString(a.hint, b.hint)) return false;
  if (!isEqual(a.conditions, b.conditions)) return false;
  return true;
};

export const getNewSchemaFieldOptionsFromValues = (
  values: EditorDefaultFormValues
): SchemaFieldOptions => {
  // Required properties
  const newSchema: SchemaFieldOptions = {
    label: values.label,
    model: values.model,
  };

  // Optional properties
  if (values.placeholder) newSchema.placeholder = values.placeholder;
  if (values.hint) newSchema.hint = values.hint;
  if (values.readOnly) newSchema.readOnly = values.readOnly;

  // Conditions
  const newConditions = getSchemaConditionsFromValues(values.conditions);
  if (newConditions.length) newSchema.visible = newConditions;

  return newSchema;
};
